/**
 * Text field component
 * Single-line text input
 */

import React from 'react';
import { FieldSchema, ValidationResult, getHelpText } from '@/config/fieldConfig';

export interface TextFieldProps {
  name: string;
  value: string;
  onChange: (value: string) => void;
  schema: FieldSchema;
  error?: ValidationResult;
  disabled?: boolean;
}

export const TextField: React.FC<TextFieldProps> = ({
  name,
  value,
  onChange,
  schema,
  error,
  disabled = false,
}) => {
  const label = typeof schema.label === 'function' ? schema.label() : schema.label;
  const placeholder = typeof schema.placeholder === 'function' ? schema.placeholder() : schema.placeholder;
  const help = getHelpText(schema.help, value);

  const borderClass = error && !error.isWarning
    ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
    : error?.isWarning
    ? 'border-yellow-500 focus:border-yellow-500 focus:ring-yellow-500'
    : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500';

  return (
    <div className="field-wrapper">
      <label htmlFor={`field-${name}`} className="block text-sm font-medium text-gray-700 mb-1">
        {label}
        {schema.required && <span className="text-red-500 ml-1">*</span>}
      </label>

      <input
        id={`field-${name}`}
        type="text"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        readOnly={schema.readonly}
        className={`
          w-full px-3 py-2 rounded-lg border
          focus:outline-none focus:ring-2
          disabled:bg-gray-100 disabled:cursor-not-allowed
          read-only:bg-gray-50 read-only:cursor-not-allowed
          ${borderClass}
        `}
      />

      {error && (
        <div className={`text-xs mt-1 ${error.isWarning ? 'text-yellow-600' : 'text-red-600'}`}>
          {error.message}
        </div>
      )}

      {help && !error && (
        <div className="text-xs text-gray-500 mt-1">
          {help}
        </div>
      )}
    </div>
  );
};

export default TextField;
